import { useState, useEffect } from "react";
import axios from "../../config/axiosInstance";
import PageHeader from "../../component/pageHeaders";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, message, Tabs } from "antd";
import { HiRefresh } from "react-icons/hi";
import moment from "moment";

import "./styles.css";
import Loadingbox from "../../component/loadingbox";
import { API } from "../../config/API";
import { DELETE } from "../../utils/apirequest";
import DataTable from "./component/datatable";

interface HighlightRecord {
  id: number;
  media: string | null;
  permalink: string | null;
  description: string | null;
  createdAt: string | null;
  publishedAt: string | null;
  is_deleted: boolean;
  is_expired: boolean;
  tags: any[];
  user: {
    id: number;
    username: string;
  } | null;
}

function HighLights() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isloading, setIsloading] = useState(false);
  const [allData, setAllData] = useState<HighlightRecord[]>([]);
  const [paginatedData, setPaginatedData] = useState<HighlightRecord[]>([]);
  const [meta, setMeta] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"active" | "expired">("active");

  // Initialize page and pageSize from URL with proper null handling
  const getInitialPage = (): number => {
    const urlQuery = new URLSearchParams(location.search);
    const pageParam = urlQuery.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  };

  const getInitialPageSize = (): number => {
    const urlQuery = new URLSearchParams(location.search);
    const limitParam = urlQuery.get("limit");
    return limitParam ? parseInt(limitParam, 10) : 10;
  };

  const [page, setPage] = useState(getInitialPage);
  const [pageSize, setPageSize] = useState(getInitialPageSize);

  useEffect(() => {
    const urlQuery = new URLSearchParams(location.search);
    const pageParam = urlQuery.get("page");
    const limitParam = urlQuery.get("limit");
    const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
    const currentPageSize = limitParam ? parseInt(limitParam, 10) : 10;
    setPage(currentPage);
    setPageSize(currentPageSize);
    loadDataWithParams(currentPage, currentPageSize);
  }, [location.search, location.pathname]);

  useEffect(() => {
    // Filter data based on active tab
    let filtered: HighlightRecord[] = [];
    if (activeTab === "active") {
      filtered = allData.filter((item) => !item.is_expired && !item.is_deleted);
    } else {
      filtered = allData.filter((item) => item.is_expired);
    }
    setMeta(filtered.length);

    // Apply pagination to filtered data
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filtered.slice(startIndex, endIndex);
    setPaginatedData(paginated);
  }, [allData, activeTab, page, pageSize]);

  const transformData = (strapiData: any[]) => {
    return strapiData.map((item: any) => {
      const createdAt = moment(item.attributes?.createdAt);
      const hoursSinceCreation = moment().diff(createdAt, "hours");
      const calculatedIsExpired = hoursSinceCreation >= 24;

      return {
        id: item.id,
        media: item.attributes?.media || null,
        permalink: item.attributes?.permalink || null,
        description: item.attributes?.description || null,
        createdAt: item.attributes?.createdAt || null,
        publishedAt: item.attributes?.publishedAt || null,
        is_deleted: item.attributes?.is_deleted || false,
        is_expired: calculatedIsExpired,
        tags: item.attributes?.tags?.data || [],
        user: item.attributes?.createdBy?.data
          ? {
            id: item.attributes.createdBy.data.id,
            username:
              item.attributes.createdBy.data.attributes?.username ||
              item.attributes.createdBy.data.username ||
              "N/A",
          }
          : null,
      };
    });
  };

  /**
   * Loads all highlights data for client-side filtering and pagination.
   * Note: This intentionally loads all data (up to 1000 items) to enable
   * proper tab-based filtering (active/expired) and client-side pagination.
   * The currentPage and currentPageSize parameters are accepted for API
   * compatibility but are not used in the fetch call.
   */
  const loadDataWithParams = (_currentPage: number, _currentPageSize: number) => {
    setIsloading(true);
    // Load all highlights for proper filtering (using large page size)
    let filters = `?pagination[page]=1&pagination[pageSize]=10000&publicationState=preview&populate=createdBy,tags&sort=createdAt:DESC`;
    let url = API.BASE_URL + API.HIGHLIGHTS + filters;
    axios
      .get(url)
      .then((response) => {
        let strapiData = response.data.data;
        // Transform Strapi nested format to flat structure
        let transformedData = transformData(strapiData);
        setAllData(transformedData);
        setIsloading(false);
      })
      .catch((err) => {
        console.error("Error loading highlights:", err);
        setIsloading(false);
        message.error("Failed to load highlights. Please try again.");
      });
  };

  const loadData = () => {
    loadDataWithParams(page, pageSize);
  };

  const onPageChange = (newPage: number, newPageSize: number) => {
    // Create fresh URLSearchParams from current location to avoid stale state
    const urlQuery = new URLSearchParams(location.search);
    urlQuery.set("page", String(newPage));
    urlQuery.set("limit", String(newPageSize));
    navigate({ search: urlQuery.toString() });
    window.scrollTo(0, 0);
    // Data will reload via useEffect when location.search changes
  };

  const handleDelete = async (record: HighlightRecord) => {
    try {
      let url = `highlights/${record.id}`;
      const res: any = await DELETE(url);
      if (res?.data?.id) {
        message.success("Item Deleted");
        loadData();
      } else {
        message.error("Something went wrong");
      }
    } catch (error: any) {
      console.error("Error deleting highlight:", error);
      message.error("Failed to delete highlight. Please try again.");
    }
  };

  const handleUnTrash = async (record: HighlightRecord) => {
    try {
      const url = API.BASE_URL + API.HIGHLIGHTS + `/${record.id}`;
      const requestObj = {
        data: {
          is_deleted: false,
        },
      };

      const response = await axios.put(url, requestObj);

      if (response.data?.data) {
        message.success("Highlight recovered successfully");
        loadData();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Error recovering highlight:", error);
      message.error(
        error?.response?.data?.error?.message ||
        "Failed to recover highlight. Please try again."
      );
    }
  };

  const onEdit = (record: HighlightRecord) => {
    // Navigate to form - edit functionality can be added later
    navigate(`/Auth/highlights/form`, { state: { editData: record } });
  };

  const handleTogglePublish = async (
    record: HighlightRecord,
    checked: boolean,
    onComplete: () => void
  ) => {
    // Optimistically update the local state immediately
    const newPublishedAt = checked ? new Date().toISOString() : null;
    setAllData((prevData) =>
      prevData.map((item) =>
        item.id === record.id
          ? { ...item, publishedAt: newPublishedAt }
          : item
      )
    );

    try {
      const url = API.BASE_URL + API.HIGHLIGHTS + `/${record.id}`;
      const requestObj = {
        data: {
          publishedAt: checked ? new Date() : null,
        },
      };

      const response = await axios.put(url, requestObj);

      if (response.data?.data) {
        message.success(
          checked ? "Highlight published successfully" : "Highlight unpublished successfully"
        );
        // Update with server response to ensure consistency
        const serverPublishedAt = response.data.data.attributes?.publishedAt || null;
        setAllData((prevData) =>
          prevData.map((item) =>
            item.id === record.id
              ? { ...item, publishedAt: serverPublishedAt }
              : item
          )
        );
        onComplete();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      message.error(
        error?.response?.data?.error?.message ||
        "Failed to update publish status. Please try again."
      );
      // Revert optimistic update by reloading data
      loadData();
      onComplete(); // Clear loading state even on error
    }
  };

  return (
    <>
      <div>
        <PageHeader
          title={"Highlights"}
          length={`${meta || 0} Highlights`}
          loader={false}
          onBack={() => navigate("/Auth")}
        >
          <div>
            <Button
              type="primary"
              ghost
              icon={<HiRefresh />}
              onClick={() => loadData()}
            >
              Refresh
            </Button>
          </div>
          <div>
            <Button
              type="primary"
              onClick={() => navigate("/Auth/highlights/form")}
            >
              Create Highlights +
            </Button>
          </div>
        </PageHeader>

        {isloading ? (
          <Loadingbox />
        ) : (
          <>
            <div style={{ padding: 4 }} />
            <Tabs
              activeKey={activeTab}
              onChange={(key: string) => {
                setActiveTab(key as "active" | "expired");
                // Reset to first page when switching tabs
                // Create fresh URLSearchParams from current location to avoid stale state
                const urlQuery = new URLSearchParams(location.search);
                urlQuery.set("page", "1");
                navigate({ search: urlQuery.toString() });
              }}
              items={[
                {
                  key: "active",
                  label: "Active",
                },
                {
                  key: "expired",
                  label: "Expired",
                },
              ]}
            />
            <DataTable
              data={paginatedData}
              meta={meta}
              page={page}
              pagesize={pageSize}
              loading={isloading}
              refresh={() => loadData()}
              onEdit={(val: HighlightRecord) => onEdit(val)}
              onDelete={(val: HighlightRecord) => handleDelete(val)}
              onTogglePublish={handleTogglePublish}
              unTrash={(val: HighlightRecord) => handleUnTrash(val)}
              onPageChange={(page: number, pageSize: number) =>
                onPageChange(page, pageSize)
              }
            />
          </>
        )}
      </div>
      <br />
      <br />
      <br />
    </>
  );
}

export default HighLights;